/**
 * Helpers de tracking via window.dataLayer (Google Tag Manager).
 *
 * NÃO instala tags do Google Ads/GA4 diretamente — apenas faz push
 * de eventos para o dataLayer, para serem consumidos pelo GTM.
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const ensureDataLayer = (): Record<string, unknown>[] => {
  if (typeof window === "undefined") return [];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/** Push genérico, seguro: nunca quebra a UX caso algo falhe. */
export const pushToDataLayer = (payload: Record<string, unknown>) => {
  try {
    const dl = ensureDataLayer();
    dl.push(payload);
  } catch (err) {
    // Silencioso: tracking nunca deve impedir navegação/redirect
    console.warn("[dataLayer] push falhou:", err);
  }
};

export type PlanTrackingKey =
  | "Landing Page Express"
  | "Site Express"
  | "Site + Landing Page";

export interface PlanTrackingInfo {
  plan_name: PlanTrackingKey;
  plan_price: number;
  currency: "BRL";
  /** slug curto usado em URLs (?plan=...) */
  slug: "landing-page-express" | "site-express" | "site-landing-page";
  /** button_location padrão para o card de pricing */
  button_location: string;
}

export const PLAN_TRACKING: Record<PlanTrackingKey, PlanTrackingInfo> = {
  "Landing Page Express": {
    plan_name: "Landing Page Express",
    plan_price: 897.0,
    currency: "BRL",
    slug: "landing-page-express",
    button_location: "pricing_section_landing_page_express",
  },
  "Site Express": {
    plan_name: "Site Express",
    plan_price: 1497.0,
    currency: "BRL",
    slug: "site-express",
    button_location: "pricing_section_site_express",
  },
  "Site + Landing Page": {
    plan_name: "Site + Landing Page",
    plan_price: 2497.0,
    currency: "BRL",
    slug: "site-landing-page",
    button_location: "pricing_section_site_landing_page",
  },
};

export const PLAN_BY_SLUG: Record<string, PlanTrackingInfo> = Object.values(
  PLAN_TRACKING,
).reduce(
  (acc, p) => {
    acc[p.slug] = p;
    return acc;
  },
  {} as Record<string, PlanTrackingInfo>,
);

/** Clique em botão de compra de plano (antes de redirecionar p/ checkout). */
export const pushBuyPlanClick = (params: {
  plan: PlanTrackingKey;
  checkout_url?: string;
  button_location?: string;
}) => {
  const info = PLAN_TRACKING[params.plan];
  if (!info) return;
  pushToDataLayer({
    event: "click_buy_plan_ad4you_pages",
    plan_name: info.plan_name,
    plan_price: info.plan_price,
    currency: info.currency,
    checkout_url: params.checkout_url ?? "",
    button_location: params.button_location ?? info.button_location,
  });
};

/** Clique em qualquer botão de WhatsApp (lead). */
export const pushWhatsAppClick = (params: {
  button_location: string;
  plan_context?: PlanTrackingKey | "geral";
}) => {
  pushToDataLayer({
    event: "click_whatsapp_ad4you_pages",
    button_location: params.button_location,
    page_path:
      typeof window !== "undefined" ? window.location.pathname : "/",
    plan_context: params.plan_context ?? "geral",
  });
};

/** Conversão de compra concluída (página /obrigado). */
export const pushPurchase = (params: {
  transaction_id: string;
  value: number;
  plan_name: PlanTrackingKey;
}) => {
  pushToDataLayer({
    event: "purchase_lp_ad4you_pages",
    transaction_id: params.transaction_id,
    value: params.value,
    currency: "BRL",
    plan_name: params.plan_name,
  });
};

/** Gera um id único e curto para a transação (fallback quando o gateway não retorna). */
export const generateTransactionId = (): string => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* noop */
  }
  return `tx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};